<script setup lang="ts">
import { computed } from 'vue';
import { posStore } from '../../../../stores/posStore';
import AsyncImage from '../../../../components/ui/form/AsyncImage.vue';
import { formatNumber } from '../../../../utils/CommonFunctions';


const pos = posStore();

const customer = computed(() => {
  return pos.selectedPosInvoice?.invoice.customer || null;
});

const deliveryAddress = computed(() => {
  if (!pos.selectedPosInvoice?.invoice.delivery_address_id) {
    return 'Not specified';
  }
  const address = pos.selectedPosInvoice?.invoice?.customer?.addresses?.find(
    (addr) => addr.id === pos.selectedPosInvoice?.invoice.delivery_address_id
  );
  return address ? address.full_address : 'Not specified';
});

function formatPrice(price: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'MVR'
  }).format(price);
}
</script>

<template>
  <div class="sales-step">
    <div class="p-4 h-full overflow-y-auto pb-20">
      <div class="mb-6">
        <h2 class="text-xl font-semibold text-neutral-900 mb-1">Order Summary</h2>
        <p class="text-sm text-neutral-600">Review your order details before completing the sale</p>
      </div>
      
      <!-- Customer Information -->
      <div class="card mb-4">
        <h3 class="font-medium text-neutral-900 mb-2">Customer</h3>
        <p class="text-neutral-900">{{ customer?.name }}</p>
        <div>
        <p class="text-neutral-600 text-sm">{{ customer?.mobile }}</p>
        <p class="text-neutral-600 text-sm">{{ Number(customer?.credit_limit) - Number(customer?.outstanding) }}</p>
        </div>
        <p class="text-neutral-600 text-sm">{{ customer?.tax_registration_no }}</p>
      </div>
      
      <!-- Delivery Information -->
      <div class="card mb-4">
        <h3 class="font-medium text-neutral-900 mb-2">Delivery Information</h3>
        <p class="text-neutral-900 mb-2">{{ deliveryAddress }}</p>
        
        <h4 class="font-medium text-neutral-900 text-sm mt-3 mb-1">Special Instructions</h4>
        <p class="text-neutral-600 text-sm">{{ pos.selectedPosInvoice?.invoice?.delivery_remarks }}</p>
      </div>
      
      <!-- Order Items -->
      <div class="card mb-4">
        <h3 class="font-medium text-neutral-900 mb-3">Items</h3>
        
        <div v-for="item in pos.selectedPosInvoice?.invoice?.items" :key="item.id" class="flex justify-between py-2 border-b border-neutral-100 last:border-0">
          <div>
            <p class="text-neutral-900">
              <AsyncImage
                    :src="item.photo"
                    :alt="item.name"
                    className="w-10 h-10 object-cover rounded-lg"
                  />
            </p>
            <p class="text-neutral-900">{{ item.name }}</p>
            <p class="text-neutral-500 text-sm">{{ formatPrice(item.price) }} × {{ item.quantity }}</p>
          </div>
          <p class="font-medium text-neutral-900">{{ formatPrice(item.price * item.quantity) }}</p>
        </div>
      </div>
      
      <!-- Payment Information -->
      <div class="card mb-4">
        <h3 class="font-medium text-neutral-900 mb-2">Payment</h3>
        <p class="text-neutral-900" v-for="p in pos.selectedPosInvoice?.invoice?.payments">
          {{ p.payment_type?.name || 'Unknown Payment Type' }}
        </p>
      </div>
      
      <!-- Order Total -->
      <div class="card bg-primary-50 border border-primary-200">
        <h3 class="font-medium text-neutral-900 mb-3">Total</h3>
        
        <div class="flex justify-between mb-2">
          <span class="text-neutral-600">Subtotal</span>
          <span class="text-neutral-900">{{ formatNumber(pos.sub_total) }}</span>
        </div>
        
        <div class="flex justify-between mb-2">
          <span class="text-neutral-600">Tax Amount</span>
          <span class="text-neutral-900">{{ formatNumber(pos.tax_amount) }}</span>
        </div>
        
        <div class="flex justify-between font-semibold text-lg">
          <span class="text-neutral-800">Total</span>
          <span class="text-primary-600">{{ formatNumber(pos.net_total) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>